import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    Tooltip,
} from "@mui/material";
import { Description, Download } from "@mui/icons-material";
import {
    useContractVersionsQueryQuery,
    usePdfDownloadMutation,
} from "../../../generated/graphql";
import { isoTimestampToUk } from "../../../utils";
import { useToast } from "../../../contexts/toast";
import { progressToast } from "../../../utils/progress-toast";
import FileSaver from "file-saver";
import { CampaignOutletContextValue } from "../form";
import { useOutletContext } from "react-router-dom";

const PAGE_SIZE = 20;

type VersionRow = NonNullable<
    NonNullable<ReturnType<typeof useContractVersionsQueryQuery>['data']>['contractVersions']
>[number];

// A stored contract version's render URL (the server render route; contract-
// version-history 3.1). Opened in a new tab from "View".
const versionUrl = (ref: string, hash: string, versionId: string) =>
    `${window.serverUri || document.location.origin}/campaign-preview/${ref}/${hash}/contract-version/${versionId}`;

// Version history for the campaign editor's Contract tab (contract-version-
// history 5.1/5.2). Lists stored versions newest-first, cursor-paginated
// ("Load older"), and on selection opens the server render route in a new tab
// (read-only; no editing controls). "Download PDF" runs the same client-side
// pdfDownload mutation the live contract uses, pointed at the version render.
const ContractVersionHistory = () => {
    const { values } = useOutletContext<CampaignOutletContextValue>();
    const { toast } = useToast();
    const [pdfDownloadFunc] = usePdfDownloadMutation();

    const [rows, setRows] = useState<VersionRow[]>([]);
    const [after, setAfter] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);

    const { data, loading } = useContractVersionsQueryQuery({
        variables: {
            campaignId: values?.id,
            after,
            first: PAGE_SIZE,
        },
        skip: !values?.id,
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (data?.contractVersions) {
            const page = data.contractVersions;
            setRows((prev) => [...prev, ...page]);
            setHasMore(page.length >= PAGE_SIZE);
        }
    }, [data]);

    const loadOlder = () => {
        setAfter(rows[rows.length - 1]?.occurredAt ?? null);
    };

    const openVersion = (v: VersionRow) => {
        if (!values?.job_reference || !values?.portfolio_hash) return;
        window.open(versionUrl(values.job_reference, values.portfolio_hash, v.id), "_blank");
    };

    const onPDF = useCallback(
        async (v: VersionRow) => {
            if (!values?.job_reference || !values?.portfolio_hash) return;
            const t = progressToast(toast)("CS_CONTRACT_VERSION_PDF");
            t.start("Generating PDF");
            try {
                const data = await pdfDownloadFunc({
                    variables: {
                        host: "preview",
                        url: `/campaign-preview/${values.job_reference}/${values.portfolio_hash}/contract-version/${v.id}`,
                        outline: true,
                        expectText: values.job_reference,
                    },
                });
                t.ok("Rendering file");
                const res = await fetch(data.data.pdfDownload.data);
                const blob = await res.blob();
                FileSaver.saveAs(blob, `${values.job_reference}-version-${v.id}.pdf`);
            } catch (e: any) {
                t.fail(e?.message ? e.message : "PDF generation failed");
            }
        },
        [pdfDownloadFunc, toast, values?.job_reference, values?.portfolio_hash]
    );

    if (loading && rows.length === 0) {
        return <Box sx={{ py: 2 }}><Typography variant="body2">Loading…</Typography></Box>;
    }
    if (!rows.length) {
        return (
            <Box sx={{ py: 2, px: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    No versions recorded yet. A version is captured when a save changes the contract.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, fontWeight: 600 }}>
                Version history
            </Typography>
            <List dense>
                {rows.map((v) => (
                    <ListItemButton key={v.id} onClick={() => openVersion(v)} disableRipple>
                        <ListItemText
                            primary={isoTimestampToUk(v.occurredAt)}
                            secondary={v.user?.name ? `by ${v.user.name}` : undefined}
                            primaryTypographyProps={{ variant: "body2" }}
                        />
                        <Tooltip title="Open read-only render">
                            <Description fontSize="small" color="action" />
                        </Tooltip>
                        <Tooltip title="Download PDF">
                            <span onClick={(e) => { e.stopPropagation(); onPDF(v); }}>
                                <Download fontSize="small" color="action" />
                            </span>
                        </Tooltip>
                    </ListItemButton>
                ))}
            </List>
            {!loading && hasMore && (
                <Box sx={{ px: 1, py: 0.5 }}>
                    <Button size="small" onClick={loadOlder}>
                        Load older
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default ContractVersionHistory;
